<template>
  <PropertyOnboardingStep
    :is-next-button-disabled="form.validation.$invalid"
    title="What are the listing basics?"
    :submit="submit"
  >
    <FormGroup>
      <FormField
        v-if="isApartmentNumberFieldRequired"
        label="Apartment number"
        :error="form.fieldErrors('apartmentNumber')"
      >
        <Input v-model="form.fields.apartmentNumber" name="apartmentNumber" placeholder="Type apartment number">
          <template #prefix>#</template>
        </Input>
      </FormField>

      <FormField label="Property Size" :error="form.fieldErrors('squareFeet')">
        <Input
          v-model="form.fields.squareFeet"
          type="number"
          min="0"
          name="squareFeet"
          placeholder="Enter a whole number, e.g. 850"
          @keydown="forbidCharacters"
        >
          <template #suffix>ft²</template>
        </Input>
      </FormField>

      <FormField label="Bedrooms" direction="row" :error="form.fieldErrors('bedrooms')">
        <InputStepper v-model="form.fields.bedrooms" :min="1" name="bedrooms" />
      </FormField>

      <FormField label="Full bathrooms" direction="row" :error="form.fieldErrors('fullBathrooms')">
        <InputStepper v-model="form.fields.fullBathrooms" :min="0" name="fullBathrooms" />
      </FormField>

      <FormField label="Half bathrooms" direction="row" :error="form.fieldErrors('halfBathrooms')">
        <InputStepper v-model="form.fields.halfBathrooms" :min="0" name="halfBathrooms" />
      </FormField>
    </FormGroup>
  </PropertyOnboardingStep>
</template>

<script setup lang="ts">
import { minValue, numeric, required, requiredIf } from '@vuelidate/validators';
import { watch, computed } from 'vue';

import { FormField, FormGroup, Input, InputStepper, useForm } from '@tallo/design-system';
import {
  SaveLocationDto,
  usePropertyStore,
  isApartmentNumberRequired,
  SavePropertySpecificationsDto,
} from '@tallo/investor/property';
import { PropertySpecificationsDto } from '@tallo/property';

import PropertyOnboardingStep from '../property-onboarding-step.vue';

const isApartmentNumberFieldRequired = computed(() => {
  return isApartmentNumberRequired(propertyStore.property?.specifications.propertyType);
});

const propertyStore = usePropertyStore();
const form = useForm<SavePropertySpecificationsDto & Pick<SaveLocationDto, 'apartmentNumber'>>(
  {
    apartmentNumber: null,
    squareFeet: null,
    bedrooms: null,
    fullBathrooms: null,
    halfBathrooms: null,
  },
  {
    apartmentNumber: { required: requiredIf(() => isApartmentNumberFieldRequired.value) },
    squareFeet: { required, numeric, minValue: minValue(1) },
    bedrooms: { required, numeric, minValue: minValue(1) },
    fullBathrooms: { required, numeric, minValue: minValue(0) },
    halfBathrooms: { required, numeric, minValue: minValue(0) },
  },
);

async function submit() {
  return await form.submit((fields) => {
    const apartmentNumber = isApartmentNumberFieldRequired.value ? fields.apartmentNumber : null;

    return Promise.all([propertyStore.saveSpecifications(fields), propertyStore.saveLocation({ apartmentNumber })]);
  });
}

const forbidCharacters = (event: KeyboardEvent) => {
  if (event.key === '.' || event.key === ',' || event.key.toLowerCase() === 'e') {
    event.preventDefault();
  }
};

function populateSpecifications(specifications?: PropertySpecificationsDto) {
  Object.assign(form.fields, {
    squareFeet: specifications?.squareFeet ?? null,
    bedrooms: specifications?.bedrooms ?? 1,
    fullBathrooms: specifications?.fullBathrooms ?? 1,
    halfBathrooms: specifications?.halfBathrooms ?? 0,
  });
}

function populateApartmentNumber(apartmentNumber?: string | null) {
  Object.assign(form.fields, { apartmentNumber: apartmentNumber ?? null });
}

watch(() => propertyStore.property?.specifications, populateSpecifications, { immediate: true });
watch(() => propertyStore.property?.location?.apartmentNumber, populateApartmentNumber, { immediate: true });
</script>

<style scoped lang="scss">
.form-field {
  width: 100%;
}
</style>
